import React, { useEffect, useMemo, useState } from 'react';
import { collection, doc, getDoc, limit, onSnapshot, orderBy, query, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useTheme } from '../contexts/ThemeContext';
import { AlertTriangle, CheckCircle2, Clock, EyeOff, Loader2, MessageSquare, Search, ShieldAlert } from 'lucide-react';
import { formatDateTime } from '../utils/formatters';
import toast from 'react-hot-toast';

const statusOptions = ['all', 'pending', 'reviewed', 'dismissed', 'action_taken'];

const statusLabels = {
  pending: 'Pending review',
  reviewed: 'Reviewed',
  dismissed: 'Dismissed',
  action_taken: 'Action taken',
};

const severityStyles = {
  high: 'bg-red-500/10 text-red-500 border-red-500/20',
  medium: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
  low: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
};

const Messages = () => {
  const { colors } = useTheme();
  const [flags, setFlags] = useState([]);
  const [selectedFlag, setSelectedFlag] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    const flagsQuery = query(
      collection(db, 'messageModerationFlags'),
      orderBy('createdAt', 'desc'),
      limit(100)
    );

    const unsubscribe = onSnapshot(flagsQuery, async (snapshot) => {
      try {
        const rows = await Promise.all(
          snapshot.docs.map(async (flagDoc) => {
            const data = flagDoc.data();
            const [sender, recipient] = await Promise.all([
              fetchUserSummary(data.senderId),
              fetchUserSummary(data.recipientId),
            ]);

            return {
              id: flagDoc.id,
              ...data,
              sender,
              recipient,
            };
          })
        );

        setFlags(rows);
        setSelectedFlag((current) => {
          if (!current) return rows[0] ?? null;
          return rows.find((row) => row.id === current.id) ?? rows[0] ?? null;
        });
      } catch (error) {
        console.error('Error loading moderation flags:', error);
        toast.error('Failed to load flagged messages');
      } finally {
        setLoading(false);
      }
    }, (error) => {
      console.error('Error listening to moderation flags:', error);
      toast.error('Failed to load flagged messages');
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const filteredFlags = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();

    return flags.filter((flag) => {
      const statusMatches = statusFilter === 'all' || (flag.status || 'pending') === statusFilter;
      if (!statusMatches) return false;
      if (!search) return true;

      return [
        flag.messageContent,
        flag.sender?.name,
        flag.sender?.email,
        flag.recipient?.name,
        flag.recipient?.email,
        ...(flag.categories || []),
        ...(flag.detectedTerms || []),
      ].some((value) => String(value || '').toLowerCase().includes(search));
    });
  }, [flags, searchTerm, statusFilter]);

  const kpis = [
    { id: 'total', label: 'Flagged Messages', value: flags.length, icon: MessageSquare, color: colors.primary },
    { id: 'pending', label: 'Pending Review', value: flags.filter((f) => (f.status || 'pending') === 'pending').length, icon: Clock, color: colors.warning },
    { id: 'high', label: 'High Severity', value: flags.filter((f) => f.severity === 'high').length, icon: ShieldAlert, color: colors.error },
  ];

  const updateFlagStatus = async (flagId, status) => {
    try {
      await updateDoc(doc(db, 'messageModerationFlags', flagId), {
        status,
        reviewedAt: new Date(),
      });
      toast.success('Moderation status updated');
    } catch (error) {
      console.error('Error updating moderation flag:', error);
      toast.error('Failed to update status');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin mx-auto mb-4" style={{ color: colors.primary }} />
          <p className="font-bold" style={{ color: colors.textSecondary }}>Loading safety alerts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn h-screen flex flex-col">
      <div className="flex-shrink-0 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ color: colors.text }}>
            Message Safety Alerts
          </h1>
          <p className="mt-1 text-[13px] uppercase tracking-wider font-bold" style={{ color: colors.textSecondary }}>
            Only messages detected by moderation rules are shown here. Normal private conversations are not browsed.
          </p>
        </div>
      </div>

      <div className="flex-shrink-0 grid grid-cols-1 sm:grid-cols-3 gap-4">
        {kpis.map((kpi) => (
          <div key={kpi.id} className="card-premium p-4" style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }}>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 flex-shrink-0 rounded-2xl flex items-center justify-center" style={{ backgroundColor: `${kpi.color}22`, color: kpi.color }}>
                <kpi.icon className="w-6 h-6" />
              </div>
              <div>
                <p className="text-[11px] font-extrabold uppercase tracking-widest" style={{ color: colors.textSecondary }}>{kpi.label}</p>
                <p className="text-2xl font-black mt-0.5" style={{ color: colors.text }}>{kpi.value}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex-1 min-h-0 flex gap-4 overflow-hidden mb-6">
        <div className="w-full lg:w-[390px] flex-shrink-0 flex flex-col card-premium overflow-hidden" style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
          <div className="p-4 space-y-3" style={{ backgroundColor: colors.backgroundSecondary, borderBottom: `1px solid ${colors.border}` }}>
            <div className="relative">
              <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 w-4 h-4" style={{ color: colors.textMuted }} />
              <input
                type="text"
                placeholder="Search flagged messages..."
                className="w-full pl-10 pr-4 py-3 rounded-xl text-[13px] font-medium focus:outline-none transition-all"
                style={{ backgroundColor: colors.background, border: `1px solid ${colors.border}`, color: colors.text }}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <select
              className="w-full px-3 py-3 rounded-xl text-[13px] font-bold focus:outline-none"
              style={{ backgroundColor: colors.background, border: `1px solid ${colors.border}`, color: colors.text }}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              {statusOptions.map((status) => (
                <option key={status} value={status}>{status === 'all' ? 'All statuses' : statusLabels[status]}</option>
              ))}
            </select>
          </div>

          <div className="flex-1 overflow-y-auto scrollbar-thin">
            {filteredFlags.length > 0 ? (
              filteredFlags.map((flag) => {
                const isSelected = selectedFlag?.id === flag.id;
                return (
                  <button
                    type="button"
                    key={flag.id}
                    className="w-full text-left p-4 cursor-pointer transition-all border-l-4 relative group"
                    style={{
                      backgroundColor: isSelected ? colors.primaryGhost : 'transparent',
                      borderBottom: `1px solid ${colors.border}`,
                      borderLeftColor: isSelected ? colors.primary : 'transparent',
                    }}
                    onClick={() => setSelectedFlag(flag)}
                  >
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="min-w-0">
                        <p className="font-bold text-[13px] truncate" style={{ color: colors.text }}>
                          {flag.sender?.name || 'Unknown user'}
                        </p>
                        <p className="text-[11px] truncate" style={{ color: colors.textSecondary }}>
                          {flag.sender?.email || flag.senderId || 'No sender info'}
                        </p>
                      </div>
                      <span className={`px-2 py-1 rounded text-[10px] font-black uppercase border ${severityStyles[flag.severity] || severityStyles.low}`}>
                        {flag.severity || 'low'}
                      </span>
                    </div>
                    <p className="text-[12px] line-clamp-2" style={{ color: colors.textSecondary }}>
                      {flag.messageContent || 'No message content stored'}
                    </p>
                    <p className="text-[10px] font-bold uppercase tracking-wider mt-2" style={{ color: colors.textMuted }}>
                      {formatDateTime(flag.createdAt)}
                    </p>
                  </button>
                );
              })
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-center p-6">
                <EyeOff className="w-10 h-10 mb-3 opacity-50" style={{ color: colors.textMuted }} />
                <p className="text-[14px] font-bold" style={{ color: colors.textSecondary }}>
                  No flagged messages match this view.
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="flex-1 flex flex-col card-premium overflow-hidden" style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
          {selectedFlag ? (
            <>
              <div className="px-6 py-4 flex items-center justify-between" style={{ borderBottom: `1px solid ${colors.border}`, backgroundColor: colors.backgroundSecondary }}>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-red-500/10 text-red-500">
                    <AlertTriangle className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-widest" style={{ color: colors.textMuted }}>
                      Flag ID: {selectedFlag.id.slice(0, 10)}
                    </p>
                    <p className="text-[15px] font-bold" style={{ color: colors.text }}>
                      {statusLabels[selectedFlag.status || 'pending'] || selectedFlag.status}
                    </p>
                  </div>
                </div>
                <span className={`px-3 py-1 rounded-full text-[11px] font-black uppercase border ${severityStyles[selectedFlag.severity] || severityStyles.low}`}>
                  {selectedFlag.severity || 'low'} severity
                </span>
              </div>

              <div className="flex-1 overflow-y-auto p-6 space-y-5" style={{ backgroundColor: colors.background }}>
                <section className="card-premium p-5" style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
                  <p className="text-[11px] font-black uppercase tracking-widest mb-3" style={{ color: colors.textSecondary }}>Flagged message</p>
                  <p className="text-[15px] leading-7 whitespace-pre-wrap" style={{ color: colors.text }}>
                    {selectedFlag.messageContent}
                  </p>
                </section>

                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                  <InfoCard title="Sender" values={[
                    selectedFlag.sender?.name || 'Unknown',
                    selectedFlag.sender?.email || selectedFlag.senderId,
                    `ID: ${selectedFlag.senderId || 'N/A'}`,
                  ]} colors={colors} />
                  <InfoCard title="Recipient" values={[
                    selectedFlag.recipient?.name || 'Unknown',
                    selectedFlag.recipient?.email || selectedFlag.recipientId,
                    `ID: ${selectedFlag.recipientId || 'N/A'}`,
                  ]} colors={colors} />
                  <InfoCard title="Detection" values={[
                    `Categories: ${(selectedFlag.categories || []).join(', ') || 'N/A'}`,
                    `Terms: ${(selectedFlag.detectedTerms || []).join(', ') || 'N/A'}`,
                    `Message time: ${formatDateTime(selectedFlag.messageCreatedAt)}`,
                  ]} colors={colors} />
                  <InfoCard title="Context IDs" values={[
                    `Thread: ${selectedFlag.threadId || 'N/A'}`,
                    `Message: ${selectedFlag.messageId || 'N/A'}`,
                    `Property: ${selectedFlag.propertyId || 'N/A'}`,
                  ]} colors={colors} />
                </div>
              </div>

              <div className="px-6 py-4 flex flex-wrap gap-3 justify-end" style={{ borderTop: `1px solid ${colors.border}`, backgroundColor: colors.backgroundSecondary }}>
                <button className="px-4 py-2 rounded-xl text-[12px] font-bold border" style={{ color: colors.text, borderColor: colors.border }} onClick={() => updateFlagStatus(selectedFlag.id, 'dismissed')}>
                  Dismiss
                </button>
                <button className="px-4 py-2 rounded-xl text-[12px] font-bold border" style={{ color: colors.text, borderColor: colors.border }} onClick={() => updateFlagStatus(selectedFlag.id, 'reviewed')}>
                  Mark reviewed
                </button>
                <button className="px-4 py-2 rounded-xl text-[12px] font-bold text-white bg-red-500" onClick={() => updateFlagStatus(selectedFlag.id, 'action_taken')}>
                  <CheckCircle2 className="w-4 h-4 inline mr-2" />
                  Action taken
                </button>
              </div>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-center p-8" style={{ backgroundColor: colors.background }}>
              <EyeOff className="w-12 h-12 opacity-30 mb-4" style={{ color: colors.textMuted }} />
              <p className="text-[18px] font-black uppercase tracking-widest" style={{ color: colors.text }}>No alert selected</p>
              <p className="text-[13px] font-medium mt-2" style={{ color: colors.textSecondary }}>Select a flagged message to review the message and user information.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const fetchUserSummary = async (userId) => {
  if (!userId) return null;

  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (!userDoc.exists()) return { name: 'Unknown user', email: 'No email' };
    const user = userDoc.data();
    return {
      name: user.displayName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Unknown user',
      email: user.email || 'No email',
    };
  } catch (error) {
    return { name: 'Unknown user', email: 'Unavailable' };
  }
};

const InfoCard = ({ title, values, colors }) => (
  <section className="card-premium p-4" style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
    <p className="text-[11px] font-black uppercase tracking-widest mb-3" style={{ color: colors.textSecondary }}>{title}</p>
    <div className="space-y-2">
      {values.map((value, index) => (
        <p key={index} className="text-[13px] break-words" style={{ color: index === 0 ? colors.text : colors.textSecondary }}>
          {value || 'N/A'}
        </p>
      ))}
    </div>
  </section>
);

export default Messages;
